// oxlint-disable react/jsx-pascal-case
/**
 * Editorial Folio SVG illustrations for empty states.
 *
 * Theme-safe: they use `currentColor` and `var(--color-*)` through consumer
 * text classes (`text-accent`, etc.), never hardcoded colors, so they work
 * correctly in every theme. Accessible: `aria-hidden` by default (the
 * semantics come from the empty-state title). Decorative detail is softened
 * with `motion-safe`/`prefers-reduced-motion`.
 */
import { type Component, type ComponentProps, splitProps } from "solid-js";
import { cn } from "@folio/shared";

type IllustrationProps = ComponentProps<"svg">;

/**
 * Common SVG wrapper: applies default viewBox/fill/aria and composes the
 * consumer class inside JSX (tracked scope) to preserve reactivity.
 */
const IllustrationSvg: Component<IllustrationProps> = (props) => {
  const [local, others] = splitProps(props, ["class", "children"]);
  return (
    <svg
      viewBox="0 0 160 120"
      fill="none"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      class={cn("h-auto w-40 max-w-full", local.class)}
      {...others}
    >
      {local.children}
    </svg>
  );
};

/** Blank page with a pen: "there are no pages yet". */
export const EmptyPagesIllustration: Component<IllustrationProps> = (props) => (
  <IllustrationSvg {...props}>
    <rect
      x="40"
      y="20"
      width="80"
      height="84"
      rx="8"
      class="fill-surface stroke-border"
      stroke-width="2"
    />
    <line
      x1="56"
      y1="44"
      x2="104"
      y2="44"
      class="stroke-muted-foreground/40"
      stroke-width="3"
      stroke-linecap="round"
    />
    <line
      x1="56"
      y1="58"
      x2="96"
      y2="58"
      class="stroke-muted-foreground/30"
      stroke-width="3"
      stroke-linecap="round"
    />
    <line
      x1="56"
      y1="72"
      x2="100"
      y2="72"
      class="stroke-muted-foreground/20"
      stroke-width="3"
      stroke-linecap="round"
    />
    <path
      d="M104 74 L124 54 a4 4 0 0 1 6 6 L110 80 L100 82 Z"
      class="fill-accent/15 stroke-accent"
      stroke-width="2.5"
      stroke-linejoin="round"
    />
    <circle cx="34" cy="30" r="3" class="fill-accent/40 motion-safe:animate-float" />
    <circle cx="128" cy="96" r="2.5" class="fill-primary/40" />
  </IllustrationSvg>
);

/** Open folder pointing left: "choose a page". */
export const SelectPageIllustration: Component<IllustrationProps> = (props) => (
  <IllustrationSvg {...props}>
    <rect
      x="44"
      y="34"
      width="84"
      height="60"
      rx="8"
      class="fill-surface stroke-border"
      stroke-width="2"
    />
    <path
      d="M44 46 v-6 a6 6 0 0 1 6-6 h22 l8 8 h42 a6 6 0 0 1 6 6 v4"
      class="fill-accent/10 stroke-accent"
      stroke-width="2"
      stroke-linejoin="round"
    />
    <path
      d="M40 70 H20 M28 62 l-8 8 8 8"
      class="stroke-accent motion-safe:animate-float"
      stroke-width="3"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <circle cx="86" cy="64" r="3" class="fill-muted-foreground/40" />
    <circle cx="100" cy="64" r="3" class="fill-muted-foreground/30" />
    <circle cx="72" cy="64" r="3" class="fill-muted-foreground/30" />
  </IllustrationSvg>
);

/** Broken page with a spark: error fallback (ErrorBoundary). Theme-aware. */
export const ErrorIllustration: Component<IllustrationProps> = (props) => (
  <IllustrationSvg {...props}>
    {/* Hoja partida en dos mitades ligeramente separadas */}
    <path
      d="M76 22 H48 a6 6 0 0 0 -6 6 v64 a6 6 0 0 0 6 6 h26 l-6 -22 l8 -18 l-8 -16 Z"
      class="fill-surface stroke-border"
      stroke-width="2"
      stroke-linejoin="round"
    />
    <path
      d="M84 22 h28 a6 6 0 0 1 6 6 v64 a6 6 0 0 1 -6 6 H86 l6 -22 l-8 -18 l8 -16 Z"
      class="fill-surface stroke-border"
      stroke-width="2"
      stroke-linejoin="round"
    />
    {/* Grieta central */}
    <path
      d="M80 22 l-6 16 l8 18 l-6 22 l6 -2"
      class="stroke-danger/70"
      stroke-width="2.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    {/* Renglones */}
    <line
      x1="54"
      y1="46"
      x2="68"
      y2="46"
      class="stroke-muted-foreground/30"
      stroke-width="3"
      stroke-linecap="round"
    />
    <line
      x1="92"
      y1="46"
      x2="108"
      y2="46"
      class="stroke-muted-foreground/30"
      stroke-width="3"
      stroke-linecap="round"
    />
    <line
      x1="54"
      y1="62"
      x2="66"
      y2="62"
      class="stroke-muted-foreground/20"
      stroke-width="3"
      stroke-linecap="round"
    />
    <line
      x1="94"
      y1="62"
      x2="108"
      y2="62"
      class="stroke-muted-foreground/20"
      stroke-width="3"
      stroke-linecap="round"
    />
    {/* Chispa decorativa */}
    <path
      d="M118 28 v-8 M122 24 h-8 M126 40 l5 -5 M30 84 l-5 5"
      class="stroke-accent motion-safe:animate-float"
      stroke-width="2.5"
      stroke-linecap="round"
    />
    <circle cx="34" cy="30" r="3" class="fill-danger/40" />
  </IllustrationSvg>
);

/** Clock/calendar with no data: analytics with no activity yet. */
export const NoDataIllustration: Component<IllustrationProps> = (props) => (
  <IllustrationSvg {...props}>
    <rect
      x="46"
      y="36"
      width="68"
      height="58"
      rx="8"
      class="fill-surface stroke-border"
      stroke-width="2"
    />
    {/* Barras de gráfico tenues */}
    <rect x="58" y="72" width="10" height="14" rx="2" class="fill-muted-foreground/20" />
    <rect x="75" y="64" width="10" height="22" rx="2" class="fill-muted-foreground/25" />
    <rect x="92" y="56" width="10" height="30" rx="2" class="fill-accent/30" />
    <line
      x1="54"
      y1="50"
      x2="106"
      y2="50"
      class="stroke-muted-foreground/20"
      stroke-width="2"
      stroke-linecap="round"
    />
    <path
      d="M80 24 v-6 M92 30 l4 -4 M68 30 l-4 -4"
      class="stroke-accent motion-safe:animate-float"
      stroke-width="2.5"
      stroke-linecap="round"
    />
  </IllustrationSvg>
);
